import React, { useState } from 'react';
import { useInput } from 'react-admin';
import { 
    TextField, 
    Box, 
    Popover, 
    IconButton,
    Typography,
} from '@mui/material';
import { Palette } from '@mui/icons-material';

interface SimpleColorPickerProps {
    source: string;
    label?: string;
    helperText?: string;
    validate?: any[];
    fullWidth?: boolean;
    sx?: any;
}

// Predefined color palette
const PRESET_COLORS = [
    '#FF0000', '#FF6B35', '#FF9800', '#FFC107', 
    '#FFEB3B', '#CDDC39', '#8BC34A', '#4CAF50',
    '#009688', '#00BCD4', '#03A9F4', '#2196F3',
    '#3F51B5', '#673AB7', '#9C27B0', '#E91E63',
    '#F44336', '#795548', '#9E9E9E', '#607D8B',
    '#000000', '#424242', '#757575', '#BDBDBD',
];

export const SimpleColorPicker: React.FC<SimpleColorPickerProps> = ({
    source,
    label,
    helperText,
    validate,
    fullWidth = true,
    sx,
}) => {
    const {
        field,
        fieldState: { error },
        formState: { isSubmitted },
    } = useInput({ source, validate });

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorSelect = (color: string) => {
        field.onChange(color);
        handleClose();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Ensure the value starts with # and is valid hex
        if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
            field.onChange(value);
        }
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', ...sx }}>
                <TextField
                    {...field}
                    onChange={handleInputChange}
                    label={label}
                    error={!!(error && (isSubmitted || field.value))}
                    helperText={(error && (isSubmitted || field.value)) ? error.message : helperText}
                    fullWidth={fullWidth}
                    placeholder="#000000"
                    InputProps={{
                        startAdornment: field.value && (
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 1,
                                    backgroundColor: field.value,
                                    border: '2px solid #E0E0E0',
                                    mr: 1,
                                }}
                            />
                        ),
                        endAdornment: (
                            <IconButton
                                size="small"
                                onClick={handleClick}
                                sx={{ ml: 1 }}
                            >
                                <Palette />
                            </IconButton>
                        ),
                        sx: { height: '56px' }
                    }}
                />
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, width: 280 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Select a color
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        mb: 2 
                    }}>
                        {PRESET_COLORS.map((color) => (
                            <Box
                                key={color}
                                onClick={() => handleColorSelect(color)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: color,
                                    border: '2px solid',
                                    borderColor: field.value === color ? '#000' : '#E0E0E0',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: 2,
                                    },
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Current: {field.value || '#000000'}
                        </Typography>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};